import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { SITE, MARKETS, SERVICES, OFFICES } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HindiContact = () => {
    const [params] = useSearchParams();
    const isAudit = params.get("intent") === "audit";

    const [form, setForm] = useState({
        name: "",
        business: "",
        phone: "",
        email: "",
        city: "",
        interest: "Growth Audit",
        message: "",
    });
    const [sending, setSending] = useState(false);

    useEffect(() => {
        document.title = "संपर्क करें | ALBL Communications — नर्मदापुरम और इतारसी";
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("कृपया अपना नाम और फ़ोन नंबर लिखें।");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("आपकी enquiry भेज दी गई है। ALBL जल्द ही आपसे संपर्क करेगी।");
            setForm({ ...form, name: "", business: "", phone: "", email: "", message: "" });
        } catch (err) {
            toast.error("अभी भेजा नहीं जा सका। कृपया कॉल या WhatsApp करें।");
        } finally {
            setSending(false);
        }
    };

    const inputCls =
        "w-full border border-slate-300 bg-white px-4 py-3.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#0D131F] transition-colors";
    const labelCls = "block text-xs font-semibold tracking-wider uppercase text-slate-500 mb-2";

    return (
        <main lang="hi">
            <section className="bg-[#0D131F] hero-grid text-white pt-32 sm:pt-40 pb-16 sm:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <Reveal>
                        <Eyebrow dark>{isAudit ? "ग्रोथ ऑडिट" : "संपर्क"}</Eyebrow>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.15] mt-5 max-w-4xl" data-testid="hi-contact-heading">
                            {isAudit ? "ग्रोथ ऑडिट लें।" : "बताइए आपका बिज़नेस आज कहाँ खड़ा है।"}
                        </h1>
                        <p className="mt-6 text-slate-400 text-base sm:text-lg max-w-2xl leading-relaxed">
                            हम दिखाएँगे कि आपकी मार्केटिंग कहाँ ग्रोथ छोड़ रही है।
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="bg-[#F8FAFC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-28 grid lg:grid-cols-12 gap-14">
                    <div className="lg:col-span-7">
                        <Reveal>
                            <form onSubmit={submit} className="bg-white border border-slate-200 p-8 sm:p-12" data-testid="hi-contact-form">
                                <Eyebrow>Enquiry भेजें</Eyebrow>
                                <div className="mt-8 grid sm:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="hcf-name" className={labelCls}>आपका नाम *</label>
                                        <input id="hcf-name" data-testid="hi-contact-form-input-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="पूरा नाम" required />
                                    </div>
                                    <div>
                                        <label htmlFor="hcf-business" className={labelCls}>बिज़नेस का नाम</label>
                                        <input id="hcf-business" data-testid="hi-contact-form-input-company" className={inputCls} value={form.business} onChange={set("business")} placeholder="आपका बिज़नेस" />
                                    </div>
                                    <div>
                                        <label htmlFor="hcf-phone" className={labelCls}>फ़ोन / WhatsApp *</label>
                                        <input id="hcf-phone" data-testid="hi-contact-form-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="आपका नंबर" required />
                                    </div>
                                    <div>
                                        <label htmlFor="hcf-email" className={labelCls}>ईमेल</label>
                                        <input id="hcf-email" type="email" data-testid="hi-contact-form-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                                    </div>
                                    <div>
                                        <label htmlFor="hcf-city" className={labelCls}>शहर / बाज़ार</label>
                                        <select id="hcf-city" data-testid="hi-contact-form-select-city" className={inputCls} value={form.city} onChange={set("city")}>
                                            <option value="">अपना बाज़ार चुनें</option>
                                            {MARKETS.map((m) => (
                                                <option key={m.name} value={m.name}>{m.name}</option>
                                            ))}
                                            <option value="Other">अन्य</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="hcf-interest" className={labelCls}>मुझे चाहिए</label>
                                        <select id="hcf-interest" data-testid="hi-contact-form-select-service" className={inputCls} value={form.interest} onChange={set("interest")}>
                                            <option value="Growth Audit">ग्रोथ ऑडिट</option>
                                            <option value="Festival Sales Campaign">फ़ेस्टिव सेल्स कैंपेन (चतुर्थी · नवरात्रि · दिवाली)</option>
                                            {SERVICES.map((s) => (
                                                <option key={s.slug} value={s.title}>{s.title}</option>
                                            ))}
                                            <option value="General Enquiry">कुछ और</option>
                                        </select>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="hcf-message" className={labelCls}>आपका बिज़नेस आज कहाँ खड़ा है?</label>
                                        <textarea id="hcf-message" rows={5} data-testid="hi-contact-form-textarea-message" className={inputCls} value={form.message} onChange={set("message")} placeholder="आप क्या बेचते हैं, और ग्रोथ में क्या रुकावट है?" />
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={sending}
                                    data-testid="hi-contact-form-submit-button"
                                    className="btn-sharp mt-8 inline-flex items-center gap-2 bg-[#0D131F] text-white text-xs font-bold tracking-[0.15em] uppercase px-10 py-4 disabled:opacity-60"
                                >
                                    {sending ? "भेजा जा रहा है…" : isAudit ? "ग्रोथ ऑडिट का अनुरोध करें" : "Enquiry भेजें"}
                                    <ArrowRight className="h-4 w-4 text-[#C5A059]" />
                                </button>
                            </form>
                        </Reveal>
                    </div>

                    <div className="lg:col-span-5 space-y-4">
                        <Reveal delay={120}>
                            <a href={SITE.phoneHref} data-testid="hi-contact-direct-phone" className="flex items-center gap-5 bg-[#0D131F] text-white p-7 btn-sharp">
                                <Phone className="h-6 w-6 text-[#C5A059] shrink-0" />
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-slate-400">ALBL को कॉल करें</p>
                                    <p className="font-display text-xl font-bold mt-1">{SITE.phone}</p>
                                </div>
                            </a>
                            <a href={SITE.whatsappPrefill} target="_blank" rel="noreferrer" data-testid="hi-contact-direct-whatsapp" className="flex items-center gap-5 bg-[#25D366] text-[#0D131F] p-7 btn-sharp">
                                <MessageCircle className="h-6 w-6 shrink-0" />
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase opacity-70">ALBL को WhatsApp करें</p>
                                    <p className="font-display text-xl font-bold mt-1">तुरंत चैट करें</p>
                                </div>
                            </a>
                            <a href={SITE.emailHref} data-testid="hi-contact-direct-email" className="flex items-center gap-5 bg-white border border-slate-200 p-7 btn-sharp">
                                <Mail className="h-6 w-6 text-[#C5A059] shrink-0" />
                                <div>
                                    <p className="text-xs tracking-[0.2em] uppercase text-slate-500">ईमेल</p>
                                    <p className="font-display text-base font-bold text-slate-900 mt-1 break-all">{SITE.email}</p>
                                </div>
                            </a>
                            <div className="bg-white border border-slate-200 p-7 space-y-5">
                                {OFFICES.map((o) => (
                                    <div key={o.label} className="flex items-center gap-5">
                                        <MapPin className="h-6 w-6 text-[#C5A059] shrink-0" />
                                        <div>
                                            <p className="text-xs tracking-[0.2em] uppercase text-slate-500">{o.label}</p>
                                            <p className="text-sm text-slate-800 mt-1">{o.value}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default HindiContact;
